import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PresentationIndustry, QuoteFile } from 'paintscout'
import { Alert, Button, Grid, InputField, makeStyles, Spinner, Typography, useMediaQuery } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import SearchIcon from '@material-ui/icons/Search'
import { gql } from 'apollo-boost'
import { useApolloClient } from '@apollo/react-hooks'
import { useSnackbar } from 'notistack'
import * as Sentry from '@sentry/core'
import { useClientOptions } from '@paintscout/ui-business'
import { getFeature } from '@paintscout/util/builder'
import { uploadFile } from '@paintscout/util/s3'
import type { AlbumPhoto, PexelsPhoto, PexelsSearchResult } from '@paintscout/util/pexels'
import { getPexelsUploadUrl, hasMoreResults, isTrustedPexelsUrl, searchWasSuccessful } from '@paintscout/util/pexels'
import { useFlags } from 'shared/components/common/FlagsProvider'
import PhotoMasonry from './PhotoMasonry'

const PER_PAGE = 30
const QUERY_STORAGE_KEY = 'pexelsQuery'

const PEXELS_SEARCH = gql`
  query PexelsSearch($query: String!, $page: Int, $perPage: Int) {
    pexelsSearch(query: $query, page: $page, perPage: $perPage) {
      totalResults
      page
      perPage
      photos {
        id
        width
        height
        alt
        photographer
        src {
          original
          large2x
          large
          medium
        }
      }
    }
  }
`

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    },
    preContent: {
      padding: theme.spacing(0, 0, 2)
    },
    scrollArea: {
      flex: 1,
      overflow: 'auto'
    },
    noResultsBlock: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      height: '100%',
      justifyContent: 'center'
    },
    loadingSpinner: {
      padding: theme.spacing(2, 0)
    }
  }),
  { name: 'StockPhotoPanel' }
)

export interface StockPhotoPanelProps {
  classes?: StyleClasses<typeof useStyles>
  /** Industry (or other seed) used for the initial search. Omitted when there's no presentation context (e.g. opened from the sidebar for library management only). */
  defaultQuery?: PresentationIndustry
  /** When re-opening for an existing stock photo, the query that originally found it. */
  specificQuery?: string
  onSelect: (newFile: QuoteFile) => void
}

/**
 * The Pexels search + results grid, extracted from StockPhotoDialog so it can be embedded
 * as a tab inside PresentationLibraryDialog (#21812) as well as rendered standalone.
 */
function StockPhotoPanel(props: StockPhotoPanelProps) {
  const classes = useStyles(props)
  const { defaultQuery: _defaultQuery, onSelect, specificQuery } = props
  const apolloClient = useApolloClient()
  const { clientId, options } = useClientOptions()
  const { s3MediaUpload } = useFlags()
  const s3UploadFeatureEnabled = getFeature({ options, path: 's3ImageUpload.enabled' })
  const { enqueueSnackbar } = useSnackbar()

  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<PexelsSearchResult>()
  const [error, setError] = useState(false)

  // Computed once via the initializer form (not on every render) since reading localStorage
  // is a side-effecting call — only the value at mount matters, later renders shouldn't re-read it.
  const [query, setQuery] = useState(() => {
    const previousQuery = typeof window !== 'undefined' ? window.localStorage.getItem(QUERY_STORAGE_KEY) : null
    return specificQuery || previousQuery || (_defaultQuery === 'other' ? 'Dinosaurs' : _defaultQuery) || ''
  })
  // Keep the latest query available inside async callbacks without re-binding listeners.
  const queryRef = useRef(query)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    queryRef.current = query
  }, [query])

  const hasMore = hasMoreResults(results)

  const runSearch = useCallback(
    async (page: number, append: boolean) => {
      const searchTerm = queryRef.current?.trim()
      if (!searchTerm) return

      setIsLoading(true)
      try {
        const { data } = await apolloClient.query<{ pexelsSearch: PexelsSearchResult }>({
          query: PEXELS_SEARCH,
          variables: { query: searchTerm, page, perPage: PER_PAGE },
          fetchPolicy: 'network-only'
        })

        const result = data?.pexelsSearch
        if (!searchWasSuccessful(result)) {
          setError(true)
          return
        }

        // Don't overwrite the user's remembered query with a specific re-open query.
        if (searchTerm !== specificQuery && typeof window !== 'undefined') {
          window.localStorage.setItem(QUERY_STORAGE_KEY, searchTerm)
        }

        setError(false)
        setResults((prev) => (append && prev ? { ...result, photos: [...prev.photos, ...result.photos] } : result))
      } catch (err) {
        Sentry.captureException(err, { tags: { pexels: 'true' } })
        setError(true)
      } finally {
        setIsLoading(false)
      }
    },
    [apolloClient, specificQuery]
  )

  // Initial search on mount.
  useEffect(() => {
    runSearch(1, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = (ev?: React.SyntheticEvent) => {
    ev?.preventDefault() // don't close the dialog on form submit
    runSearch(1, false)
  }

  const handleLoadMore = useCallback(() => {
    if (isLoading || !results || !hasMoreResults(results)) return
    const nextPage = Math.floor(results.photos.length / PER_PAGE) + 1
    runSearch(nextPage, true)
  }, [isLoading, results, runSearch])

  // Keep the latest handleLoadMore available to the scroll listener without re-attaching it —
  // handleLoadMore's own deps (isLoading/results) churn on every page load, and re-registering
  // the listener mid-scroll on a state update risks missing a scroll event.
  const handleLoadMoreRef = useRef(handleLoadMore)
  useEffect(() => {
    handleLoadMoreRef.current = handleLoadMore
  }, [handleLoadMore])

  // Minimal infinite scroll: load more as the content nears the bottom.
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !hasMore) return

    const onScroll = () => {
      if (el.scrollHeight - el.scrollTop - 5 <= el.clientHeight) handleLoadMoreRef.current()
    }
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [hasMore])

  const handleSelect = useCallback(
    async (photo: PexelsPhoto) => {
      setIsLoading(true)
      try {
        // The shared uploadFile() orchestrator (same one FileDropArea/useFileDropArea use) does
        // a real binary PUT for the S3 path, so it needs an actual File/Blob in hand — unlike
        // Cloudinary, S3 has no server-side "fetch this remote URL" step. That means a
        // client-side download-then-reupload round trip (vs. Cloudinary's single server-side
        // fetch) — accepted as the cost of S3 support, since Pexels photos are already
        // compressed/resized here.
        const remoteUrl = getPexelsUploadUrl(photo.src)
        // Defense-in-depth: `photo.src` comes from our own pexelsSearch resolver, but never let
        // a tampered/forged src drive the browser into fetching (and re-uploading) an arbitrary
        // URL.
        if (!isTrustedPexelsUrl(remoteUrl)) throw new Error('Refusing to fetch an untrusted photo URL')
        const response = await fetch(remoteUrl)
        if (!response.ok) throw new Error(`Failed to fetch Pexels photo (${response.status})`)
        const blob = await response.blob()
        const file = new File([blob], `pexels-${photo.id}.jpg`, { type: blob.type || 'image/jpeg' })

        const quoteFile = await uploadFile({
          file,
          apolloClient,
          uploadOptions: {
            companyId: clientId,
            docId: String(photo.id),
            s3UploadFeatureEnabled,
            s3MediaUploadEnabled: s3MediaUpload
          }
        })

        onSelect({ ...quoteFile, pexelsQuery: queryRef.current })
      } catch (err) {
        Sentry.captureException(err, { tags: { pexels: 'true' } })
        enqueueSnackbar('Unable to add photo. Please try again.', { variant: 'error' })
      } finally {
        setIsLoading(false)
      }
    },
    [apolloClient, clientId, s3UploadFeatureEnabled, s3MediaUpload, onSelect, enqueueSnackbar]
  )

  const isSm = useMediaQuery('sm')
  const isMd = useMediaQuery('md')
  const columns = isSm ? 2 : isMd ? 3 : 4

  const albumPhotos: AlbumPhoto[] = useMemo(
    () =>
      (results?.photos ?? []).map((photo) => ({
        key: String(photo.id),
        src: photo.src.large || photo.src.medium || photo.src.original,
        width: photo.width ?? 1,
        height: photo.height ?? 1,
        alt: photo.alt,
        photographer: photo.photographer,
        onClick: () => handleSelect(photo)
      })),
    [results, handleSelect]
  )

  const noResults = searchWasSuccessful(results) && results.photos.length === 0

  return (
    <div className={classes.root}>
      <div className={classes.preContent}>
        <form onSubmit={handleSearch}>
          <Grid container spacing={2}>
            <Grid item xs>
              <InputField
                autoFocus
                fullWidth
                label={'Search Images'}
                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setQuery(ev.target.value)}
                value={query}
              />
            </Grid>
            <Grid item xs style={{ alignItems: 'flex-end', display: 'flex', flexGrow: 0 }}>
              <Button
                disabled={isLoading || !query}
                variant={'contained'}
                color={'primary'}
                style={{ minWidth: 39, width: 39, height: 39 }}
                type={'submit'}
              >
                <SearchIcon />
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
      <div className={classes.scrollArea} ref={scrollRef}>
        {isLoading && !results ? (
          <Spinner className={classes.loadingSpinner} fullWidth fullHeight />
        ) : error ? (
          <div className={classes.noResultsBlock}>
            <Alert variant={'text'} severity={'error'}>
              There was an error searching photos. Please try again.
            </Alert>
          </div>
        ) : noResults ? (
          <div className={classes.noResultsBlock}>
            <Typography variant={'overline'}>{'No results :('}</Typography>
            <Typography variant={'overline'}>Try another search term</Typography>
          </div>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <PhotoMasonry photos={albumPhotos} columns={columns} />
            </Grid>
            {isLoading && (
              <Grid item xs={12}>
                <Spinner className={classes.loadingSpinner} fullWidth />
              </Grid>
            )}
            {!hasMore && albumPhotos.length > 0 && (
              <Grid item xs={12}>
                <Typography
                  style={{ display: 'block', textAlign: 'center', marginTop: 16, marginBottom: 8 }}
                  variant={'overline'}
                >
                  {'End of results'}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </div>
    </div>
  )
}

export default StockPhotoPanel
